import api from './api';

export interface EquityPoint { t: number; equity: number; dd: number; }
export interface PerSymbolStat { symbol: string; netPct: number; netAmt: number; trades: number; winRate: number; }
export interface MonthlyPoint { ym: string; pct: number; }

export interface ComboMetrics {
  trades: number; winRate: number; expectancyPct: number; profitFactor: number;
  avgDays: number; medianDays: number; exposurePct: number; maxConsecLosses: number;
  pctGoodWithin: number; longTrades: number; shortTrades: number;
  longExpectancyPct: number; shortExpectancyPct: number; vsBuyHoldNetAmt: number;
  totalNetAmt?: number; maxDrawdownPct?: number; maxDrawdownAmt?: number;
  sharpe?: number; sortino?: number; cagrPct?: number;
}
export interface TradeRow {
  symbol: string; exchange: string; side: 'LONG' | 'SHORT';
  entryTime: number; entryPrice: number; exitTime: number; exitPrice: number;
  netRetPct: number; grossRetPct: number; barsHeld: number; daysHeld: number;
  mfePct: number; maePct: number; exitReason: string;
}
export interface ComboResult {
  id: string; entry: string; exit: string; params: Record<string, number>;
  metrics: ComboMetrics; robustness: number; fracPosSymbols: number; medSymExp: number; nSymbols: number;
  oos: { trainExpectancy: number; testExpectancy: number; testProfitFactor: number; degraded: boolean; testTrades: number };
  equityCurve?: EquityPoint[]; buyHoldCurve?: EquityPoint[]; perSymbol?: PerSymbolStat[];
  monthly?: MonthlyPoint[]; tradeReturns?: number[]; trades?: TradeRow[]; tradesTruncated?: boolean;
}
export interface TfReportJson {
  tf: number; htfMin: number; symbolsUsed: number;
  symbolsSkipped: Array<{ symbol: string; reason: string }>;
  medianBars: number; medianSpanDays: number; buyHoldAvgPct: number; goodProfitPct: number;
  baseline: ComboResult | null; recommended: ComboResult | null; ranked: ComboResult[];
  whyRanked: string; caveats: string[];
}
export interface BacktestResultJson {
  generatedAt: string; source: string; symbolCount: number; costModel: any;
  fixedAmount: number; preset: string; trainTestSplit: number; perTf: TfReportJson[];
}
export interface BacktestRunRow {
  id: number; label?: string; strategyEngine: string;
  status: 'QUEUED' | 'RUNNING' | 'DONE' | 'FAILED' | 'CANCELLED';
  phase?: string; progressPct: number; progressDetail?: string;
  createdAt: string; startedAt?: string; finishedAt?: string;
  config?: any; summary?: any; result?: BacktestResultJson; error?: string;
}
export interface BacktestOptions {
  strategies: Array<{ id: number; name: string; engine: string }>;
  watchlists: Array<{ id: number; name: string; count: number; symbols: Array<{ symbol: string; exchange: string }> }>;
  timeframes: number[]; presets: string[];
  defaults: any;
  sources: Array<{ value: string; label: string }>;
}

export const backtestService = {
  getOptions: () => api.get<any>('/backtests/options').then(r => r.data?.data as BacktestOptions),
  start: (config: any) => api.post<any>('/backtests', config).then(r => r.data?.data as { id: number; status: string; label: string }),
  list: () => api.get<any>('/backtests').then(r => (r.data?.data || []) as BacktestRunRow[]),
  get: (id: number) => api.get<any>(`/backtests/${id}`).then(r => r.data?.data as BacktestRunRow),
  cancel: (id: number) => api.post<any>(`/backtests/${id}/cancel`).then(r => r.data),
  remove: (id: number) => api.delete<any>(`/backtests/${id}`).then(r => r.data),
};
